import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core'
import { Chart, ChartData, ChartDataSets, ChartPoint, ChartTooltipItem } from 'chart.js'
import * as pluginAnnotations from 'chartjs-plugin-annotation'
import { RecoveryPointAttemptDto } from 'src/app/services/dtos'
import { dateDiff, durationText } from 'src/app/shared/utils'

function hasReplicationLatency(rpaDto: RecoveryPointAttemptDto) {
  return rpaDto.maxReplicationLatency !== null && rpaDto.maxReplicationLatency !== undefined
}
@Component({
  selector: 'rpo-chart',
  templateUrl: './rpo-chart.component.html',
  styleUrls: ['./rpo-chart.component.scss'],
  standalone: false,
})
export class RpoChartComponent implements OnInit, OnChanges {
  constructor() {}

  private get displayRpoLine() {
    return this.rpo > 0
  }

  private get successfulRecoveryPointAttempts() {
    return this.recoveryPointAttempts.filter((rpa) => !!rpa.createSuccess)
  }

  @Input() rpo: number = null
  @Input() recoveryPointAttempts: Array<RecoveryPointAttemptDto> = null
  @ViewChild('chart', { static: true }) chartElement: ElementRef = null
  chart: Chart = null
  private showReplicationLatencies = false

  // this line loads the Annotations plugin.  Don't delete it, even though
  // the actual variable is never referenced.
  lineChartPlugins = [pluginAnnotations]

  ngOnInit() {
    this.initializeChart()
  }

  ngOnChanges(changes: SimpleChanges) {
    this.showReplicationLatencies = this.rpo === 0 && this.recoveryPointAttempts.some(hasReplicationLatency)
    if (!!this.chart && !!this.recoveryPointAttempts) {
      this.initializeChart()
    }
  }

  private initializeChart() {
    if (!!this.chart) {
      this.chart.destroy()
    }

    // We'll scale the graph such that a certain percentage of data points fit below a certain vertical mark on the graph.
    // For snapshots, we'll place that at 75% of the way up the graph.  If the RPO is 0, we'll then draw an annotation
    // line horizontally at that marker and show it as the X% snapshot line.  If the RPO is > 0, we'll draw a horizontal
    // line at the RPO.  Further, if the RPO is 0, we may show a second dataset showing the replication latency, and
    // draw another horizontal line showing where X% of those datapoints lie.
    const snapshotPercent = 0.9
    const replicationPercent = snapshotPercent

    // We'll orient the y-axis scale around the 90% snapshot line, placing that at 60% of the overall height.
    // However, if that means the RPO is not on the chart, we'll re-orient the y-axis scale around the RPO line,
    // placing that at 80% of the overall height.
    const snapshotPercentLine = this.computeXPercentSnapshotDataScale(snapshotPercent) // orient the y-axis around the 90% snapshot time
    const snapshotYMax = snapshotPercentLine / 0.6 // snapshot line at 60% the overall height.

    // Y-axis max needs to account for scenarios where the snapshot performance is much better than the RPO.  This
    // happens if the RPO has recently changed and historical snapshots were happening more frequently.  It also happens
    // if the application is on the same sync pair as other applications with more aggressive RPOs.
    const rpoYMax = this.rpo / 0.8 // rpo line at 80% of the overall height.
    const ymax = Math.max(snapshotYMax, rpoYMax)

    // Initialize the chartSettings as a scatter graph with 1 dataset on 1 y-axis.  This is the dataset for the
    // snapshot replication age.  This is shown whether or not real-time replication is in play.
    const chartSettings = {
      type: 'scatter',
      data: {
        datasets: [
          {
            data: [],
            lineTension: 0,
            pointRadius: 0,
            pointHitRadius: 5,
            showLine: true,
            fill: 'origin' as string | boolean,
            borderWidth: 2 as undefined | number,
            yAxisID: 'snapshot-age',
          },
        ],
      },
      options: {
        responsive: true,
        showLines: true,
        legend: {
          display: false,
        },
        scales: {
          xAxes: [
            {
              type: 'time',
              display: false,
              id: 'date',
            },
          ],
          yAxes: [
            {
              display: false,
              id: 'snapshot-age',
              ticks: {
                beginAtZero: true,
                max: ymax,
              } as { beginAtZero?: boolean; min?: number; max?: number },
            },
          ],
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem: ChartTooltipItem, data: ChartData) => {
              const val = (data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index] as ChartPoint).y as number
              const tips = []
              if (tooltipItem.datasetIndex === 0) {
                if (this.displayRpoLine) {
                  tips.push(`Recovery Point Age: ${durationText(val)}`)
                } else {
                  tips.push(`Snapshot Age: ${durationText(val)}`)
                }
              } else {
                tips.push(`Replication Latency: ${durationText(val, true)}`)
              }
              return tips
            },
            title: (tooltipItem: ChartTooltipItem, data: ChartData) => {
              const date = (data.datasets[tooltipItem[0].datasetIndex].data[tooltipItem[0].index] as ChartPoint)
                .x as Date
              return `${date.toLocaleString()}`
            },
          },
          intersect: true,
          caretPadding: 10,
          displayColors: false,
        },
      },
    }

    // Add another data series if we're also displaying the real-time replication latency.  This only displays
    // if real-time replication is in play _and_ there is some replication latency data to show.
    let realTimeMax = 0
    if (this.showReplicationLatencies) {
      realTimeMax = this.computeXPercentRealTimeDataScale(replicationPercent)
      let y_scale_max = realTimeMax / 0.25 // We want the 90% line to show at 1/4th the way up the graph.
      let y_scale_min = 0
      if (y_scale_max === 0) {
        // The y-scale needs to go from 0 to something greater than zero.  If all latency data points are zero, we need
        // to force the scale max to be greater than zero.  We'll fudge the max to 1 (any number should do).
        // We'll also fudge the min to be above the base of the graph so that we see some sort of series
        // on the graph here.
        y_scale_max = 1
        y_scale_min = y_scale_max * -0.05
      }

      // Add the second dataset (empty) to the chart
      chartSettings.data.datasets.push({
        data: [],
        lineTension: 0,
        pointRadius: 0,
        pointHitRadius: 5,
        showLine: true,
        fill: 'start',
        borderWidth: 2,
        yAxisID: 'replication-latency',
      })
      // Add the second data scale to the chart
      chartSettings.options.scales.yAxes.push({
        display: false,
        id: 'replication-latency',
        ticks: {
          min: y_scale_min,
          max: y_scale_max,
        },
      })
    }

    // Add an annotation line for the RPO if we're not doing real-time replication
    if (this.displayRpoLine) {
      // @ts-ignore
      chartSettings.options.annotation = {
        drawTime: 'afterDatasetsDraw',
        annotations: [
          {
            id: 'rpo-line',
            type: 'line',
            mode: 'horizontal',
            scaleID: 'snapshot-age',
            value: this.rpo,
            borderColor: '#5C5C5C',
            borderWidth: 1,
            borderDash: [3, 3],
            label: {
              enabled: true,
              fontColor: '#5C5C5C',
              fontFamily: "'Source Sans Pro', sans-serif",
              content: `RPO: ${durationText(this.rpo)}`,
              position: 'right',
              yAdjust: -7,
              backgroundColor: 'transparent',
            },
          },
        ],
      }
    }
    // If we are doing real-time replication, add an annotation line at the P90 value for snapshot age.
    // And if we have real-time replication latency data, add another snapshot line at the P90 value for replication latency.
    else {
      // @ts-ignore
      chartSettings.options.annotation = {
        drawTime: 'afterDatasetsDraw',
        annotations: [
          {
            id: 'snapshot-line',
            type: 'line',
            mode: 'horizontal',
            scaleID: 'snapshot-age',
            value: snapshotPercentLine,
            borderColor: '#5C5C5C',
            borderWidth: 1,
            borderDash: [3, 3],
            label: {
              enabled: true,
              fontColor: '#5C5C5C',
              fontFamily: "'Source Sans Pro', sans-serif",
              content: `${snapshotPercent * 100}% Snapshot Age: ${durationText(snapshotPercentLine, true)}`,
              position: 'right',
              yAdjust: -7,
              backgroundColor: 'transparent',
            },
          },
        ],
      }
      if (this.showReplicationLatencies) {
        // @ts-ignore
        chartSettings.options.annotation.annotations.push({
          id: 'latency-line',
          type: 'line',
          mode: 'horizontal',
          scaleID: 'replication-latency',
          value: realTimeMax,
          borderColor: '#5C5C5C',
          borderWidth: 1,
          borderDash: [3, 3],
          label: {
            enabled: true,
            fontColor: '#5C5C5C',
            fontFamily: "'Source Sans Pro', sans-serif",
            content: `${replicationPercent * 100}% Replication Latency: ${durationText(realTimeMax, true)}`,
            position: 'right',
            yAdjust: -7,
            backgroundColor: 'transparent',
          },
        })
      }
    }

    this.chart = new Chart(this.chartElement.nativeElement, chartSettings)
    this.loadChartData()
  }

  private loadChartData() {
    this.loadSnapshotChartData()
    this.loadRealTimeChartData()
    this.chart.update()
  }

  private loadSnapshotChartData() {
    const rpoData = this.computeRpoData()
    this.chart.data.datasets[0].data.length = 0
    rpoData.forEach((point) => this.chart.data.datasets[0].data.push(point))
  }

  private computeRpoData() {
    if (!this.recoveryPointAttempts) {
      return []
    }

    const successfulAttempts = this.successfulRecoveryPointAttempts
    let data = []
    // if the application has been around for less than 10 RPO cycles, we draw a sawtooth.
    // otherwise, we just draw the peaks.
    const rpoCycleThreshold = 10
    const dateThreshold = new Date(Date.now() - rpoCycleThreshold * this.rpo * 1000)

    if (successfulAttempts.length > 0 && successfulAttempts[0].createSuccess > dateThreshold) {
      data = this.computeBriefRpoData(successfulAttempts, dateThreshold)
    } else if (successfulAttempts.length > 0) {
      data = this.computeNotBriefRpoData(successfulAttempts)
    }

    return data
  }

  private computeBriefRpoData(successfulAttempts: Array<RecoveryPointAttemptDto>, dateThreshold: Date) {
    const data: Array<{ x: Date; y: number }> = []

    // insert a null value at the dateThreshold to establish the lower bounds of the x-axis
    data.push({ x: dateThreshold, y: null })
    for (let i = 0; i < successfulAttempts.length - 1; i++) {
      // insert 1 datapoint for when the RP was published
      data.push({
        x: successfulAttempts[i].createSuccess,
        y: dateDiff(successfulAttempts[i].createSuccess, successfulAttempts[i].timestamp) / 1000,
      })
      // insert a second datapoint for when the RP was replaced
      data.push({
        x: successfulAttempts[i + 1].createSuccess,
        y: dateDiff(successfulAttempts[i + 1].createSuccess, successfulAttempts[i].timestamp) / 1000,
      })
    }

    // insert datapoints for the latest RP.  The algorithm is slightly different.
    const latestAttempt = successfulAttempts[successfulAttempts.length - 1]
    data.push({
      x: latestAttempt.createSuccess,
      y: dateDiff(latestAttempt.createSuccess, latestAttempt.timestamp) / 1000,
    })

    const now = new Date()
    data.push({
      x: now,
      y: dateDiff(now, latestAttempt.timestamp) / 1000,
    })

    return data
  }

  private computeNotBriefRpoData(successfulAttempts) {
    const data: Array<{ x: Date; y: number }> = []
    for (let i = 0; i < successfulAttempts.length - 1; i++) {
      data.push({
        x: successfulAttempts[i + 1].createSuccess,
        y: dateDiff(successfulAttempts[i + 1].createSuccess, successfulAttempts[i].timestamp) / 1000,
      })
    }

    // insert one final datapoint for now
    const now = new Date()
    data.push({
      x: now,
      y: dateDiff(now, successfulAttempts[successfulAttempts.length - 1].timestamp) / 1000,
    })

    return data
  }

  private computeXPercentSnapshotDataScale(x: number) {
    const successfulAttempts = this.successfulRecoveryPointAttempts
    const snapshotAges: Array<number> = []
    for (let i = 1; i < successfulAttempts.length; i++) {
      snapshotAges.push(dateDiff(successfulAttempts[i].createSuccess, successfulAttempts[i - 1].timestamp) / 1000)
    }
    snapshotAges.sort((l, r) => l - r)
    const xPercentElement = Math.floor(snapshotAges.length * x)
    return snapshotAges[xPercentElement]
  }

  private loadRealTimeChartData() {
    if (this.showReplicationLatencies) {
      const replicationData = this.computeRealTimeData()
      this.chart.data.datasets[1].length = 0
      replicationData.forEach((point) => this.chart.data.datasets[1].data.push(point))
    }
  }

  private computeRealTimeData() {
    const data: Array<{ x: Date; y: number }> = []
    for (let i = 0; i < this.recoveryPointAttempts.length; i++) {
      const attempt = this.recoveryPointAttempts[i]
      if (hasReplicationLatency(attempt)) {
        const publishTime = attempt.createSuccess ? attempt.createSuccess : attempt.createFailure
        data.push({
          x: publishTime,
          y: attempt.maxReplicationLatency,
        })
      }
    }
    return data
  }

  private computeXPercentRealTimeDataScale(x: number) {
    // We want the real-time scale to sit below the non-real-time scale (since it should be faster).  We'll target having X% of
    // the real-time metrics sit at 25% on the graph.  So, this function computes the X% value, and multiplies that * 4
    // for the scale.

    // sort the data.  Array.sort() in Javscript mutates the array, so we use Array.slice() to copy it first.
    const filteredRealTimeData = this.recoveryPointAttempts.filter(hasReplicationLatency)
    const sortedRealTimeData = filteredRealTimeData.sort((l, r) => l.maxReplicationLatency - r.maxReplicationLatency)
    const ninetyPercentElement = Math.floor(sortedRealTimeData.length * x)
    return sortedRealTimeData[ninetyPercentElement].maxReplicationLatency
  }
}
